import type {
  RequestRatingDetailResponse,
  RequestRatingEditBody,
} from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { invalidateStatusChangeQueries } from "@/features/requests/utils/requestInvalidation";
import { projectsKeys, projectUsersKeys, requestKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

export function useUpdateRequestRating(requestId: null | number) {
  const queryClient = useQueryClient();

  const { isPending, mutate } = useMutation<
    RequestRatingDetailResponse,
    { status?: number },
    RequestRatingEditBody
  >({
    mutationFn: (body) =>
      apiClient.url(`/requests/${requestId}/rating`).patch(body).json(),
    onError: (error) => {
      const isConflict = error?.status === 409;

      if (isConflict && requestId != null) {
        void queryClient.invalidateQueries({
          queryKey: requestKeys.rating(requestId),
        });
      }

      toast.add({
        description: isConflict
          ? "This rating was changed by someone else. We reloaded the latest version."
          : "The rating could not be updated. Please try again.",
        timeout: 4000,
        title: isConflict ? "Rating changed" : "Update failed",
        type: "error",
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: requestKeys.all });
      void queryClient.invalidateQueries({ queryKey: projectUsersKeys.all });
      void queryClient.invalidateQueries({ queryKey: projectsKeys.all });
      invalidateStatusChangeQueries(queryClient);
    },
  });

  return { isPending, updateRating: mutate };
}
